import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider } from '@radix-ui/react-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Home from './pages/Home';
import Profile from './pages/Profile';
import CaptchaVerification from './pages/CaptchaVerification';
import Room from './pages/Room';
import { initializeAndCheckSession, supabase } from './lib/supabase';
import { Toast, ToastTitle, ToastDescription, ToastViewport, ToastAction } from '@radix-ui/react-toast';

// Componente para inicializar Supabase antes de renderizar la aplicación
const InitializeApp = ({ children }: { children: React.ReactNode }) => {
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptCount, setAttemptCount] = useState(0);

  useEffect(() => {
    const init = async () => {
      try {
        console.log('Inicializando y verificando sesión de Supabase...');
        const success = await initializeAndCheckSession();
        
        if (success) {
          console.log('Sesión de Supabase inicializada correctamente');
          setInitialized(true);
        } else {
          // Si no hubo éxito pero no se lanzó una excepción, intentamos de nuevo
          if (attemptCount < 2) { // Reducir a 2 intentos totales (original + 1 reintento)
            console.log(`Reintentando inicialización (intento ${attemptCount + 1}/2)...`);
            setAttemptCount(prev => prev + 1);
            
            // Esperar 1 segundo antes de reintentar
            setTimeout(() => {
              init();
            }, 1000);
          } else {
            console.log('No se pudo inicializar Supabase después de 2 intentos. Continuando con funcionalidad limitada...');
            // Inicializar de todos modos para permitir funcionalidad limitada
            setInitialized(true);
          }
        }
      } catch (err) {
        console.error('Error al inicializar Supabase:', err);
        setError('Error al conectar con el servidor. La aplicación funcionará con capacidades limitadas.');
        setInitialized(true); // Permitir que la app se muestre de todos modos
      }
    };

    init();
  }, []);

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3">Iniciando aplicación...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 text-white p-4">
        <h2 className="text-xl font-bold mb-2">Advertencia</h2>
        <p className="mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="px-5 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white cursor-pointer"
        >
          Reintentar
        </button>
        <button 
          onClick={() => {
            setError(null);
            setInitialized(true);
          }}
          className="mt-2 px-5 py-2 bg-gray-600 hover:bg-gray-700 rounded text-white cursor-pointer"
        >
          Continuar de todos modos
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

// Componente de notificación para errores de autenticación
const AuthErrorNotification = () => {
  const [showAuthError, setShowAuthError] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState('');
  
  // Función para mostrar error de autenticación
  const displayAuthError = (message: string) => {
    setAuthErrorMessage(message);
    setShowAuthError(true);
    
    // Redirigir a la página de inicio de sesión después de 2 segundos
    setTimeout(() => {
      window.location.href = '/login';
    }, 2000);
  };
  
  // Escuchar eventos personalizados de error de autenticación
  useEffect(() => {
    const handleAuthError = (event: CustomEvent) => {
      displayAuthError(event.detail?.message || 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
    };
    
    // Crear un evento personalizado para la comunicación
    window.addEventListener('supabase-auth-error' as any, handleAuthError as EventListener);
    
    return () => {
      window.removeEventListener('supabase-auth-error' as any, handleAuthError as EventListener);
    };
  }, []);
  
  if (!showAuthError) return null;
  
  return (
    <Toast open={showAuthError} onOpenChange={setShowAuthError} className="fixed bottom-4 right-4 bg-red-600 text-white p-4 rounded shadow-lg max-w-md z-50">
      <ToastTitle className="font-bold">Error de autenticación</ToastTitle>
      <ToastDescription>{authErrorMessage}</ToastDescription>
      <ToastAction asChild altText="Cerrar">
        <button onClick={() => setShowAuthError(false)} className="ml-2 bg-red-700 px-2 py-1 rounded text-sm">
          Cerrar
        </button>
      </ToastAction>
    </Toast>
  );
};

// Componente para las rutas de la aplicación
function AppRoutes() {
  const { user, profile, hasProfile, loading } = useAuth();
  
  // Aplicar el tema del usuario cuando carga la aplicación
  useEffect(() => {
    const userTheme = profile?.theme || 'dark';
    document.body.classList.remove('theme-dark', 'theme-neon', 'theme-retro');
    document.body.classList.add(`theme-${userTheme}`);
  }, [profile]);

  // Si todavía estamos cargando la autenticación, mostrar un estado de carga
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3">Verificando autenticación...</p>
      </div>
    );
  }

  return (
    <ToastProvider>
      <div className="min-h-screen">
        {/* Componente para notificar errores de autenticación */}
        <AuthErrorNotification />
        <ToastViewport className="fixed bottom-0 right-0 flex flex-col p-6 gap-2 w-96 max-w-[100vw] m-0 list-none z-50" />
        
        <Routes>
          {/* La ruta raíz redirige según estado de autenticación */}
          <Route 
            path="/" 
            element={user ? <Navigate to="/home" replace /> : <Navigate to="/verify" replace />} 
          />
          
          {/* Ruta para la verificación CAPTCHA */}
          <Route 
            path="/verify" 
            element={user ? <Navigate to="/home" replace /> : <CaptchaVerification />} 
          />
          
          {/* Ruta de autenticación */}
          <Route 
            path="/login" 
            element={user ? <Navigate to="/home" replace /> : <Login />} 
          />
          
          {/* Rutas principales, solo accesibles para usuarios autenticados */}
          <Route 
            path="/home" 
            element={user ? <Home /> : <Navigate to="/login" replace />} 
          />
          
          <Route 
            path="/profile" 
            element={user ? <Profile /> : <Navigate to="/login" replace />}
          />
          
          {/* Ruta para las salas */}
          <Route 
            path="/room/:id"
            element={user ? <Room /> : <Navigate to="/login" replace />}
          />
          
          {/* Redirección para rutas no encontradas */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </ToastProvider>
  );
}

// Componente principal de la aplicación
function App() {
  return (
    <Router>
      <InitializeApp>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </InitializeApp>
    </Router>
  );
}

export default App; 